import * as THREE from "three";
import { Renderer } from "../core/renderer.js";
import { SceneManager } from "../core/scene_manager.js";
import { StressTest } from "./stress_test.js";

/**
 * Top-right HUD for stress levels: what one frame costs the GPU (triangles and
 * draw calls from renderer.info, shadow pass included), the polygons sitting in
 * the scene, block count and FPS. Runs its own rAF loop while a stress level is
 * active and removes itself when stress mode ends.
 */
export class StressRenderHud
{
  private static readonly REFRESH_MS = 500;

  private static node: HTMLDivElement | null = null;
  private static running = false;
  private static frames = 0;
  private static window_start = 0;
  private static calls_max = 0;
  private static tris_max = 0;

  public static start(): void
  {
    if ( this.running )
      return;

    this.running = true;
    this.frames = 0;
    this.window_start = performance.now();
    this.calls_max = 0;
    this.tris_max = 0;
    requestAnimationFrame( this.tick );
  }

  private static readonly tick = ( now: number ): void =>
  {
    const hud = StressRenderHud;
    if ( !StressTest.isActive() )
    {
      hud.stop();
      return;
    }

    const render = Renderer.renderer?.info.render;
    if ( render )
    {
      hud.calls_max = Math.max( hud.calls_max, render.calls );
      hud.tris_max = Math.max( hud.tris_max, render.triangles );
    }

    hud.frames++;
    const elapsed = now - hud.window_start;
    if ( elapsed >= hud.REFRESH_MS )
    {
      hud.draw( render, hud.frames * 1000 / elapsed );
      hud.frames = 0;
      hud.window_start = now;
      hud.calls_max = 0;
      hud.tris_max = 0;
    }

    requestAnimationFrame( hud.tick );
  };

  private static stop(): void
  {
    this.running = false;
    this.node?.remove();
    this.node = null;
  }

  private static draw( render: THREE.WebGLInfo["render"] | undefined, fps: number ): void
  {
    if ( !this.node )
    {
      this.node = document.createElement( "div" );
      this.node.style.cssText = "position:fixed;top:8px;right:8px;z-index:100000;pointer-events:none;"
        + "background:rgba(0,0,0,0.72);color:#fff;font:12px/1.35 monospace;padding:6px 8px;border-radius:6px;white-space:pre";
      document.body.appendChild( this.node );
    }

    const scene = this.countScene();
    const format = ( value: number ) => value.toLocaleString( "en-US" );
    this.node.textContent = [
      StressTest.getName(),
      `blocks      ${StressTest.getBlockCount()}`,
      `fps         ${fps.toFixed( 0 )}`,
      `draw calls  ${render ? render.calls : "-"}  (max ${this.calls_max})`,
      `tris/frame  ${render ? format( render.triangles ) : "-"}  (max ${format( this.tris_max )})`,
      `scene polys ${format( scene.triangles )}  in ${scene.meshes} meshes`,
    ].join( "\n" );
  }

  /** Triangles of every visible mesh in the world (instances counted), independent of what the camera culls. */
  private static countScene(): { triangles: number; meshes: number }
  {
    let triangles = 0;
    let meshes = 0;

    SceneManager.world?.traverseVisible( object =>
    {
      const mesh = object as THREE.Mesh;
      if ( !mesh.isMesh )
        return;

      const geometry = mesh.geometry as THREE.BufferGeometry;
      const vertices = geometry.index ? geometry.index.count : geometry.getAttribute( "position" )?.count ?? 0;
      const instances = ( mesh as THREE.InstancedMesh ).isInstancedMesh ? ( mesh as THREE.InstancedMesh ).count : 1;
      triangles += Math.floor( vertices / 3 ) * instances;
      meshes++;
    } );

    return { triangles, meshes };
  }
}
